import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { teamAPI } from '../services/api';
import { toast } from 'sonner';
import { Heart, ArrowLeft } from 'lucide-react';

const CreateTeam = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    care_recipient_name: '',
    care_recipient_dob: '',
    care_recipient_address: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        care_recipient_name: formData.care_recipient_name,
        care_recipient_dob: formData.care_recipient_dob || null,
        care_recipient_address: formData.care_recipient_address || null
      };

      await teamAPI.create(payload);
      toast.success('Team created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl p-8 glass-effect">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-8 h-8 text-blue-600" fill="currentColor" />
            <h1 className="text-3xl font-bold">Create Care Team</h1>
          </div>
          <p className="text-gray-600">
            Set up a team to coordinate care for your loved one.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="name">Team Name *</Label>
            <Input
              id="name"
              data-testid="team-name-input"
              placeholder="e.g., Mom's Care Team"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Team Description</Label>
            <Textarea
              id="description"
              data-testid="team-description-input"
              placeholder="Brief description of the team's purpose"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="border-t pt-6">
            <h2 className="text-xl font-semibold mb-4">Care Recipient Information</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="care_recipient_name">Name *</Label>
                <Input
                  id="care_recipient_name"
                  data-testid="recipient-name-input"
                  placeholder="Care recipient's name"
                  value={formData.care_recipient_name}
                  onChange={(e) => setFormData({ ...formData, care_recipient_name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="care_recipient_dob">Date of Birth (Optional)</Label>
                <Input
                  id="care_recipient_dob"
                  type="date"
                  data-testid="recipient-dob-input"
                  value={formData.care_recipient_dob}
                  onChange={(e) => setFormData({ ...formData, care_recipient_dob: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="care_recipient_address">Current Address (Optional)</Label>
                <Textarea
                  id="care_recipient_address"
                  data-testid="recipient-address-input"
                  placeholder="Full address"
                  value={formData.care_recipient_address}
                  onChange={(e) => setFormData({ ...formData, care_recipient_address: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
            disabled={loading}
            data-testid="create-team-submit-btn"
          >
            {loading ? 'Creating...' : 'Create Team'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default CreateTeam;